import callAPI from './call'

/**
 * The 'call' namespace (within the 'api' type) is used to make audio and video calls to and from
 * SIP users and PSTN phones.
 *
 * Call functions are all part of the 'call' namespace.
 *
 * @public
 * @namespace call
 */

/**
 * Information about a Call.
 *
 * Can be retrieved using the {@link call.getAll} or {@link call.getById} APIs.
 *
 * @public
 * @static
 * @typedef {Object} CallObject
 * @memberof call
 * @requires cpaas_call
 * @property {string} id The ID of the call.
 * @property {user.UserID} from A unique identifier (uri) of the person who made the call.
 * @property {user.UserID} to A unique identifier (uri) of the person who receives the call.
 * @property {string} direction The direction in which the call was created. Can be 'outgoing' or 'incoming'.
 * @property {string} state The current state of the call. See {@link call.states} for possible states.
 * @property {string} mediaConnectionState The current status of the call's media connection. See {@link call.mediaConnectionStates} for possible states.
 * @property {boolean} localHold Indicates whether this call is currently being held locally.
 * @property {boolean} remoteHold Indicates whether this call is currently being held remotely.
 * @property {Array<string>} localTracks A list of Track IDs that the call is sending to the remote participant.
 * @property {Array<string>} remoteTracks A list of Track IDs that the call is receiving from the remote participant.
 * @property {call.MediaOffered} [mediaOffered] Information about what media was offered by the person who made the call.
 * @property {Object} remoteParticipant Information about the other call participant.
 * @property {string} [remoteParticipant.displayNumber] The User ID of the remote participant in the form "username@domain".
 * @property {string} [remoteParticipant.displayName] The display name of the remote participant.
 * @property {call.BandwidthControls} bandwidth The bandwidth limitations set for the call.
 * @property {number} startTime The start time of the call in milliseconds since the epoch.
 * @property {number} [endTime] The end time of the call in milliseconds since the epoch.
 */

/**
 * Information about a Call.
 *
 * Can be retrieved using the {@link call.getAll} or {@link call.getById} APIs.
 *
 * @public
 * @static
 * @module CallObject
 * @typedef {Object} CallObject
 * @memberof call
 * @requires link_call
 * @requires callMe
 * @property {string} id The ID of the call.
 * @property {user.UserID} from A unique identifier (uri) of the person who made the call.
 * @property {user.UserID} to A unique identifier (uri) of the person who receives the call.
 * @property {string} direction The direction in which the call was created. Can be 'outgoing' or 'incoming'.
 * @property {string} state The current state of the call. See {@link call.states} for possible states.
 * @property {string} mediaConnectionState The current status of the call's media connection. See {@link call.mediaConnectionStates} for possible states.
 * @property {boolean} localHold Indicates whether this call is currently being held locally.
 * @property {boolean} remoteHold Indicates whether this call is currently being held remotely.
 * @property {Array<string>} localTracks A list of Track IDs that the call is sending to the remote participant.
 * @property {Array<string>} remoteTracks A list of Track IDs that the call is receiving from the remote participant.
 * @property {call.MediaOffered} [mediaOffered] Information about what media was offered by the person who made the call.
 * @property {Object} remoteParticipant Information about the other call participant.
 * @property {string} [remoteParticipant.displayNumber] The User ID of the remote participant in the form "username@domain".
 * @property {string} [remoteParticipant.displayName] The display name of the remote participant.
 * @property {call.BandwidthControls} bandwidth The bandwidth limitations set for the call.
 * @property {Array<call.CustomParameter>} customParameters The locally set Custom Parameters for the call.
 * @property {number} startTime The start time of the call in milliseconds since the epoch.
 * @property {number} [endTime] The end time of the call in milliseconds since the epoch.
 */

/**
 * The 'media' namespace provides an interface for interacting with Media that the
 *    SDK has access to. Media is used in conjunction with the {@link call Calls}
 *    feature to manipulate and render the Tracks sent and received from a Call.
 *
 * Media and Track objects are not created directly, but are created as part of
 *    Call operations. Media and Tracks will either be marked as "local" or
 *    "remote" depending on whether their source is the local user's machine
 *    or a remote user's machine.
 *
 * The Media feature also keeps track of media devices that the user's machine
 *    can access. Any media device (eg. USB headset) connected to the machine
 *    can be used as a source for media. Available devices can be found using
 *    the {@link media.getDevices} API.
 *
 * @public
 * @namespace media
 */

/**
 * The MediaConstraint type defines the format for configuring media options.
 * Either the `exact` or `ideal` property should be provided. If both are present, the
 *    `exact` value will be used.
 *
 * When the `exact` value is provided, it will be the only value considered for the option.
 *    If it cannot be used, the constraint will be considered an error.
 *
 * When the `ideal` value is provided, it will be considered as the optimal value for the option.
 *    If it cannot be used, the closest acceptable value will be used instead.
 *
 * A string value can be provided directly instead of using the MediaConstraint format.
 *    Using a string directly is not recommended, since behaviour may differ depending
 *    on browser and media property. For most properties, a direct string value will be
 *    handled as `ideal` behaviour, but some properties may follow the `exact` behaviour
 *    (eg. `deviceId`).
 *
 * @public
 * @static
 * @typedef {Object} MediaConstraint
 * @memberof call
 * @property {string} [exact] The required value for the constraint. Other values will not be accepted.
 * @property {string} [ideal] The ideal value for the constraint. Other values will be considered if necessary.
 * @example
 * // Specify video resolution when making a call.
 * client.call.make(destination, {
 *    audio: true,
 *    video: true,
 *    videoOptions: {
 *      // Set height and width constraints to ideally be 1280x720.
 *      height: { ideal: 720 },
 *      width: { ideal: 1280 }
 *    }
 * })
 */

/**
 * The MediaOffered type defines what media capabilities are offered by the person making a call.
 * This is an optional property and therefore may be null if it is not known or if it's associated with caller's side of the call.
 *
 * @public
 * @static
 * @typedef {Object} MediaOffered
 * @memberof call
 * @property {boolean} audio Specifies if any audio capability has been offered by the caller. If set to true, then the caller is capable of supporting at least one audio stream in the current call.
 * @property {boolean} video Specifies if any video capability has been offered by the caller. If set to true, then the caller is capable of supporting at least one video stream in the current call.
 */

/**
 * The BandwidthControls type defines the format for configuring media and/or track bandwidth options.
 * BandwidthControls only affect received remote tracks of the specified type.
 *
 * @public
 * @static
 * @typedef {Object} BandwidthControls
 * @memberof call
 * @property {number} [call] The desired combined bandwidth bitrate in kilobits per second for all media in the call.
 * @property {number} [audio] The desired bandwidth bitrate in kilobits per second for received remote audio.
 * @property {number} [video] The desired bandwidth bitrate in kilobits per second for received remote video.
 * @example
 * // Specify received remote video bandwidth limits when making a call.
 * client.call.make(destination, mediaConstraints,
 *  {
 *    bandwidth: {
 *      video: 5
 *    }
 *  }
 * )
 */

/**
 * The DSCPControls type defines the format for configuring network priorities (DSCP marking) for the media traffic.
 *
 * If DSCPControls are not configured for a call the network priority of the traffic for all media kinds will be the default (i.e., "low").
 *
 * @public
 * @static
 * @typedef {Object} DSCPControls
 * @memberof call
 * @property {RTCPriorityType} [audioNetworkPriority='low'] The desired network priority for audio traffic (see {@link https://www.w3.org/TR/webrtc-priority/#rtc-priority-type RTCPriorityType Enum} for the list of possible values).
 * @property {RTCPriorityType} [videoNetworkPriority='low'] The desired network priority for video traffic (see {@link https://www.w3.org/TR/webrtc-priority/#rtc-priority-type RTCPriorityType Enum} for the list of possible values).
 * @property {RTCPriorityType} [screenNetworkPriority='low'] The desired network priority for screen share traffic (see {@link https://www.w3.org/TR/webrtc-priority/#rtc-priority-type RTCPriorityType Enum} for the list of possible values).
 */

/**
 * Configuration options for an RTCPeerConnection.
 * It represents an RTCPeerConfiguration dictionary, whose parameters are documented {@link https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/RTCPeerConnection#parameters here}.
 * @public
 * @static
 * @typedef {Object} RTCPeerConnectionConfig
 * @memberof call
 * @param {Array<RTCIceServer>} [iceServers] The list of ICE servers to be used for calls.
 *   The full set of properties defined for an RTCIceServer, are mentioned
 *   {@link https://developer.mozilla.org/en-US/docs/Web/API/RTCIceServer#properties here}.
 * @param {string} [sdpSemantics='unified-plan'] The sdpSemantics to use (`'unified-plan'` or `'plan-b'`).
 *   As 'plan-b' has become a deprecated option, it will therefore be removed in the future.
 * @param {number} [iceCandidatePoolSize] An unsigned 16-bit integer value which specifies
 *   the size of the prefetched ICE candidate pool. The default value is 0 (meaning no candidate prefetching will occur).
 * @param {string} [iceTransportPolicy] The current ICE transport policy; if the policy isn't specified,
 *   'all' is assumed by default. Possible values are: 'all', 'public', 'relay'.
 * @param {string} [bundlePolicy] For further description on this and other properties,
 *   see {@link https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/RTCPeerConnection#parameters RTCPeerConnection's configuration parameters}.
 */

/**
 * @public
 * @static
 * @typedef {Object} IceServer
 * @memberof call
 * @property {Array<string>|string} urls Either an array of URLs for reaching out several ICE servers or a single URL for reaching one ICE server. See {@link https://developer.mozilla.org/en-US/docs/Web/API/RTCIceServer/urls RTCIceServers.urls documentation} to learn more about the actual url format.
 * Starting with Chromium 110, TURN(S) urls must only contain a transport
 * parameter in the query section and STUN urls must not specify any query section.
 * @property {string} [credential] The credential needed by the ICE server.
 * @property {string} [username] The credential needed by the ICE server.
 */

/**
 * This object is provided to the {@link call.IceCollectionCheckFunction IceCollectionCheckFunction}, and contains the
 *  necessary information about the call (i.e., call ID, current call operation), and information about the ongoing ICE collection,
 *  such as the list of all ICE candidates collected so far and the ICE gathering state.
 *
 * @public
 * @static
 * @typedef {Object} IceCollectionInfo
 * @memberof call
 * @property {string} callId The ID of the call.
 * @property {string} callOperation The current operation of the call.
 * @property {string} reason The reason the check function was called. Three possible values:
 *  'NewCandidate' - A new ICE candidate was collected. Note: there may be multiple new ICE candidates collected.
 *  'IceGatheringStateChanged' - The ICE gathering state changed.
 *  'Scheduled' - A scheduled call (for first invocation, and subsequent invocations based on `wait` value returned by {@link call.IceCollectionCheckFunction IceCollectionCheckFunction}.)
 * @property {Array<RTCIceCandidate>} iceCandidates An array of all ICE candidates collected so far.
 * @property {number} iceCollectionDuration The time elapsed since the start of the ICE collection process.
 * @property {string} iceGatheringState The current ICE gathering state.
 *    See {@link https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/iceGatheringState RTCPeerConnection.iceGatheringState}.
 * @property {Object} rtcPeerConnectionConfig The current configration for the RTCPeerConnection.
 * @property {string} rtcLocalSessionDescription The current local session description set on the peer.
 */

/**
 * The form of the ICE collection check function, the arguments that it receives, and the outputs expected.
 *
 * This function is provided the necessary details of the current WebRTC session and ICE collection
 * ({@link call.IceCollectionInfo IceCollectionInfo}), which it can use to dictate how to proceed with a call.
 *  The function can be invoked for three different reasons:
 *  a new ICE candidate was collected, the ICE gathering state changed, or a scheduled call based on the `wait` time set after
 *  an initial invocation of the function.
 *
 * The function must then return an appropriate result object in the format of {@link call.IceCollectionCheckResult IceCollectionCheckResult}
 *  which will dictate how the call will proceed. An incorrect return object, or result `type`, will cause the call to end with an error.
 *
 * [Default]
 * The default IceCollectionCheckFunction uses the following algorithm to determine if the call can proceed to negotiation:
 * 1. If the `iceGatheringState` is "complete" at any stage, then:
 *    - Proceed with the negotiation if any ICE candidates are collected.
 *    - Or, end the call if there are no ICE candidates collected.
 * 2. Otherwise, if before the ideal ICE collection timeout:
 *    - If every media has a relay ICE candidate for every configured TURN server, proceed with negotiation.
 *    - Else, wait until the ideal timeout, or when invoked for another reason.
 * 3. Otherwise, if before the max ICE collection timeout, but after the ideal ICE collection timeout:
 *    - If every media has atleast one relay ICE candidate, proceed with negotiation.
 *    - Else, wait until max timeout, or when invoked for another reason.
 * 4. Otherwise, if we are at, or past, the max ICE collection timeout:
 *    - If there are any ICE candidates collected, proceed with negotiation.
 *    - Else, end the call with an error.
 *
 * The ideal and max timeouts can be configured with the `idealIceCollectionTimeout` and `maxIceCollectionTimeout` properties of
 *  the call config.
 * @public
 * @static
 * @typedef {Function} IceCollectionCheckFunction
 * @memberof call
 * @param {call.IceCollectionInfo} iceCollectionInfo Information about the current status of the ICE candidate collection.
 * @param {Object} iceTimeouts Configurations provided to the SDK for ICE collection timeout boundaries.
 * @param {number} iceTimeouts.iceCollectionIdealTimeout The amount of time to wait for ideal candidates, in
 *      milliseconds.  See {@link config#config.call config.call} for more information.
 * @param {number} iceTimeouts.iceCollectionMaxTimeout The maximum amount of time to wait for ICE collection,
 *      in milliseconds. See {@link config#config.call config.call} for more information.
 * @returns {call.IceCollectionCheckResult} Information on how to proceed with the call and/or ICE collection.
 * @example
 * function isRelayCandidate (candidate) {
 * // NOTE: This would need to be different for Firefox since the `.type` property doesn't exist
 * // and we would need to parse it ourselves in the `.candidate` property.
 *   return candidate.type === 'relay'
 * }
 *
 * function myIceCollectionCheck ({ iceGatheringState, iceCandidates }, iceTimeouts) {
 *   if (iceGatheringState === 'complete') {
 *     if (iceCandidates.some(isRelayCandidate)) {
 *       return { type: 'StartCall' }
 *     } else {
 *       return { type: 'Error', error: new Error('Failed to start call because there is no relay candidates.') }
 *     }
 *   } else {
 *     return { type: 'Wait' }
 *   }
 * }
 */

/**
 * @public
 * @static
 * @typedef {Object} IceCollectionCheckResult
 * @memberof call
 * @property {string} type Indicates how the system should proceed with the call operation / ICE collection. The possible values are:
 *    'StartCall' - instruct the system to start the call with the currently gathered ICE candidates and other information.
 *    'Error' - instruct the system to fail the call with an error. The error to communicate to the user should be specified in the `error` property.
 *    'Wait' - instruct the system to wait for the specified amount of time before triggering a new ICE collection check. The amount
 *        of wait time should be specified in the `wait` property.
 * @property {string} error An error to be sent the user when the `type` of the result is `IceCollectionCheckResultType.Error`
 * @property {number} wait The amount of time (in milliseconds) to wait before triggering a new ICE collection check. This is only
 *    valid if the `type` of result is `IceCollectionCheckResultType.Wait`. If a value is not provided, the ICE collection check function
 *    will only be triggered for new candidates or when the ICE gathering state changes.
 */

/**
 * @public
 * @static
 * @typedef {Object} SdpHandlerInfo
 * @memberof call
 * @property {string} callId The id corresponding to the call for which the handler is being run.
 * @property {RTCSdpType} type The session description's type.
 * @property {string} step The step that will occur after the SDP Handlers are run.
 *    Will be either 'set' (the SDP will be set locally) or 'send' (the SDP will
 *    be sent to the remote endpoint).
 * @property {string} endpoint Which end of the connection created the SDP.
 */

/**
 * The form of an SDP handler function and the expected arguments that it receives.
 * @public
 * @static
 * @typedef {Function} SdpHandlerFunction
 * @memberof call
 * @param {Object} newSdp The SDP so far (could have been modified by previous handlers).
 * @param {call.SdpHandlerInfo} info Additional information that might be useful when making SDP modifications.
 * @param {Object} originalSdp The SDP in its initial state.
 * @returns {Object} The resulting modified SDP based on the changes made by this function.
 */

/**
 * The state representation of a Media object.
 * Media is a collection of Track objects.
 *
 * @public
 * @static
 * @typedef {Object} MediaObject
 * @memberof call
 * @property {string} id The ID of the Media object.
 * @property {boolean} local Indicator on whether this media is local or remote.
 * @property {Array<call.TrackObject>} tracks A list of Track objects that are contained in this Media object.
 */

/**
 * A Track is a stream of audio or video media from a single source.
 * Tracks can be retrieved using the Media module's `getTrackById` API and manipulated with other functions of the Media module.
 *
 * @public
 * @static
 * @typedef {Object} TrackObject
 * @memberof call
 * @property {Array<string>} containers The list of CSS selectors that were used to render this Track.
 * @property {boolean} disabled Indicator of whether this Track is disabled or not. If disabled, it cannot be re-enabled.
 * @property {boolean} isLocal Indicator of whether this Track is a locally created one or is a remote one.
 * @property {string} id The ID of the Track.
 * @property {string} kind The kind of Track this is (audio, video).
 * @property {string} label The label of the device this Track uses.
 * @property {boolean} muted Indicator on whether this Track is muted or not.
 * @property {boolean} sourceMuted Indicator on whether this Track is receiving media from its source or not.
 *    When true, the Track can be considered removed. This indicator is affected by actions outside the
 *    control of the SDK, such as the remote endpoint of a Call stopping to send media for a remote Track,
 *    or the browser temporarily disabling the SDK's access to a local Track's source.
 * @property {string} state The state of this Track. Can be 'live' or 'ended'.
 * @property {string} streamId The ID of the Media Stream that includes this Track.
 */

/**
 * A collection of media devices and their information.
 *
 * @public
 * @static
 * @typedef {Object} DevicesObject
 * @memberof call
 * @property {Array<call.DeviceInfo>} camera A list of camera device information.
 * @property {Array<call.DeviceInfo>} microphone A list of microphone device information.
 * @property {Array<call.DeviceInfo>} speaker A list of speaker device information.
 */

/**
 * Contains information about a device.
 *
 * @public
 * @static
 * @typedef {Object} DeviceInfo
 * @memberof call
 * @property {string} deviceId The ID of the device.
 * @property {string} groupId The group ID of the device. Devices that share a `groupId` belong to the same physical device.
 * @property {string} kind The type of the device (audioinput, audiooutput, videoinput).
 * @property {string} label The name of the device.
 */

/**
 * Custom SIP headers can be used to convey additional information to a SIP endpoint.
 *
 * These parameters must be configured on the server prior to making a request, otherwise the request will fail when trying to include the parameters.
 *
 * These parameters can be specified with the {@link call.make} and {@link call.answer} APIs.
 * They can also be set after a Call is established using the {@link call.setCustomParameters} API, and sent using the {@link call.sendCustomParameters} API.
 *
 * Custom headers may be received anytime throughout the duration a call. A remote endpoint may send custom headers when starting a call,
 *  answering a call, or during call updates such as hold/unhold and addition/removal of media in the call.
 *  When these custom headers are received, the SDK will emit a {@link call.event:call:customParameters call:customParameters} event
 *  which will contain the custom parameters that were received.
 *
 * A Call's custom parameters are stored on the Call's {@link call.CallObject CallObject},
 *  which can be retrieved using the {@link call.getById} or
 *  {@link call.getAll} APIs. These are the parameters that will be sent to the remote
 *  endpoint of the Call. Parameters received from a Call are not stored as
 *  part of the CallObject and are only provided via the {@link call.event:call:customParameters call:customParameters} event.
 *
 * @public
 * @static
 * @module CustomParameter
 * @typedef {Object} CustomParameter
 * @memberof call
 * @requires link_call
 * @requires callMe
 * @property {string} name - The name of the custom parameter
 * @property {string} value - The value of the custom parameter
 * @example
 * // Specify custom parameters when making a call.
 * client.call.make(destination, mediaConstraints,
 *  {
 *    customParameters: [
 *      {
 *        name: 'X-GPS',
 *        value: '42.686032,23.344565'
 *      }
 *    ]
 *  }
 * )
 */

/**
 * Custom SIP bodies can be used to convey additional information to a SIP endpoint.
 *
 * The appropriate configuration for sending custom bodies must be enabled on the server prior to making call requests,
 *  otherwise the requests will fail when trying to include the bodies.
 *
 * The `contentType` of the custom body should be compliant with RFC 2045, RFC 3261 (and their updated versions),
 *  and the `contentDisposition` should be compliant with RFC 2183, RFC 3204, and RFC 3261 (and their updated versions).
 *
 * These custom bodies can be specified with the {@link call.make} and {@link call.answer} APIs.
 *  They can also be set after a Call is established using the {@link call.setCustomParameters} API.
 *
 * A Call's custom bodies are stored on the Call's {@link call.CallObject CallObject},
 *  which can be retrieved using the {@link call.getById} or
 *  {@link call.getAll} APIs. These custom bodies will be sent to the remote endpoint of the Call for all subsequent
 *  basic midcall operations, such as hold/unhold and add/remove media.
 *
 * @private
 * @static
 * @module CustomBody
 * @typedef {Object} CustomBody
 * @memberof call
 * @requires link_call
 * @requires callMe
 * @property {string} contentType - The media type of the message body.
 * @property {string} contentDisposition - How the message body is to be interpreted.
 * @property {string} body - The message body.
 * @example
 * // Specify custom bodies when making a call.
 * client.call.make(destination, mediaConstraints,
 *  {
 *    customBodies: [
 *      {
 *        contentType: 'text/plain;charset=UTF-8',
 *        contentDisposition: 'render;HANDLING=OPTIONAL',
 *        body: 'Geo location information is sent.'
 *      }
 *    ]
 *  }
 * )
 */

/**
 * Call API index.
 * APIs are organized by their namespacing.
 * @method api
 * @param {Object} context
 * @param {Function} context.dispatch The redux store's dispatch function.
 * @param {Function} context.getState The redux store's getState function.
 * @return {Object} The plugin's API object.
 */
export default function api (context) {
  return {
    call: callAPI(context)
  }
}
